import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { usePonds, useMasterData } from '../../hooks/useApi';
import { retailSaleApi, sellerApi, Seller, CartonEntry } from '../../utils/retailSalesApi';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const cartonSchema = yup.object({
  carton_number: yup.string().optional(),
  pond_id: yup.string().required('Pond is required'),
  species: yup.string().required('Species is required'),
  quantity_kg: yup.number().required('Quantity is required').positive('Quantity must be positive'),
  rate_per_kg: yup.number().required('Rate is required').positive('Rate must be positive'),
  use_auto_rate: yup.boolean().optional(),
});

const schema = yup.object({
  sale_date: yup.string().required('Sale date is required'),
  seller_id: yup.string().required('Seller is required'),
  ponds: yup.array().of(yup.string()).min(1, 'At least one pond is required'),
  cartons: yup.array().of(cartonSchema).min(1, 'At least one fish crate is required'),
  amount_received: yup.number().min(0, 'Amount cannot be negative'),
  remarks: yup.string().optional(),
});

interface OnFarmSaleFormData {
  sale_date: string;
  seller_id: string;
  ponds: string[];
  cartons: CartonEntry[];
  amount_received: number;
  remarks?: string;
}

const deriveDefaultRatesFromCartons = (cartons: CartonEntry[]): Record<string, number> => {
  const rates: Record<string, number> = {};
  cartons.forEach((carton) => {
    if (!carton?.species) return;
    const rate = Number(carton.rate_per_kg) || 0;
    if (rate > 0 && (!rates[carton.species] || rates[carton.species] === 0)) {
      rates[carton.species] = rate;
    }
  });
  return rates;
};

const OnFarmSaleForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [allSellers, setAllSellers] = useState<Seller[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateSeller, setShowCreateSeller] = useState(false);
  const [showDefaultRates, setShowDefaultRates] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [newSellerName, setNewSellerName] = useState('');
  const [newSellerMobile, setNewSellerMobile] = useState('');
  const [newSellerEmail, setNewSellerEmail] = useState('');
  const [newSellerAddress, setNewSellerAddress] = useState('');
  const [creatingSeller, setCreatingSeller] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [draftSaleId, setDraftSaleId] = useState<string | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(true); // Track if we're loading draft data
  const isLoadingDraftRef = useRef(true); // Ref to track loading state for sync prevention
  
  // Default rates per species (Step 1.1) - only for species added in crates
  const [defaultRates, setDefaultRates] = useState<Record<string, number>>({});
  
  // Collapsible steps - collapse step 1 and 2 when step 3 is visible
  const [step1Collapsed, setStep1Collapsed] = useState(false);
  const [step2Collapsed, setStep2Collapsed] = useState(false);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: fishSpecies } = useMasterData('fish_species', selectedLocation?.id || null);
  
  // Deduplicate fish species by name (case-insensitive)
  const uniqueFishSpecies = useMemo(() => {
    if (!fishSpecies || fishSpecies.length === 0) return [];
    const seen = new Set<string>();
    const unique: typeof fishSpecies = [];
    
    for (const species of fishSpecies) {
      const nameLower = (species.name || '').toLowerCase().trim();
      if (nameLower && !seen.has(nameLower)) {
        seen.add(nameLower);
        unique.push(species);
      }
    }
    
    return unique;
  }, [fishSpecies]);

  const form = useForm<OnFarmSaleFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      sale_date: new Date().toISOString().split('T')[0],
      seller_id: '',
      ponds: [],
      cartons: [],
      amount_received: 0,
      remarks: '',
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'cartons',
  });

  const watchedCartons = form.watch('cartons');
  const watchedAmountReceived = form.watch('amount_received');
  const watchedPonds = form.watch('ponds');
  const watchedSellerId = form.watch('seller_id');
  
  // Auto-collapse steps 1 and 2 when step 3 becomes visible
  useEffect(() => {
    if (watchedPonds.length > 0) {
      setStep1Collapsed(true);
      setStep2Collapsed(true);
    }
  }, [watchedPonds.length]);

  // Get unique species from cartons
  const getSpeciesInCartons = (): string[] => {
    const speciesSet = new Set<string>();
    watchedCartons.forEach(c => {
      if (c.species && c.species.trim()) {
        speciesSet.add(c.species);
      }
    });
    return Array.from(speciesSet);
  };

  // Update default rates when new species is added to cartons
  useEffect(() => {
    const speciesInCartons = getSpeciesInCartons();
    const updatedRates = { ...defaultRates };
    let hasNewSpecies = false;
    
    speciesInCartons.forEach(species => {
      if (!(species in updatedRates)) {
        updatedRates[species] = 0;
        hasNewSpecies = true;
      }
    });
    
    if (hasNewSpecies) {
      setDefaultRates(updatedRates);
      // Auto-open the default rates accordion when a new species is added
      setShowDefaultRates(true);
    }
  }, [watchedCartons]);

  // Apply auto rates to cartons when default rates change
  useEffect(() => {
    watchedCartons.forEach((carton, index) => {
      if (carton.use_auto_rate && carton.species && defaultRates[carton.species] && defaultRates[carton.species] > 0) {
        form.setValue(`cartons.${index}.rate_per_kg`, defaultRates[carton.species]);
      }
    });
  }, [defaultRates, watchedCartons, form]);

  // Calculate totals
  const totalAmount = watchedCartons.reduce((sum, carton) => {
    const qty = Number(carton.quantity_kg) || 0;
    const rate = Number(carton.rate_per_kg) || 0;
    return sum + (qty * rate);
  }, 0);

  const amountDue = totalAmount - (watchedAmountReceived || 0);

  // Update carton amounts when quantity or rate changes
  useEffect(() => {
    watchedCartons.forEach((carton, index) => {
      const qty = Number(carton.quantity_kg) || 0;
      const rate = Number(carton.rate_per_kg) || 0;
      const amount = qty * rate;
      if (carton.amount !== amount) {
        form.setValue(`cartons.${index}.amount`, amount);
      }
    });
  }, [watchedCartons, form]);

  // Get cartons grouped by pond
  const getCartonsByPond = (pondId: string) => {
    return watchedCartons.filter(c => c.pond_id === pondId);
  };

  // Get next carton number for a pond
  const getNextCartonNumber = (pondId: string) => {
    const pondCartons = getCartonsByPond(pondId);
    return pondCartons.length + 1;
  };

  const [hasInitialized, setHasInitialized] = useState(false);

  // Load draft data from server on mount (only once)
  useEffect(() => {
    if (!selectedLocation?.id || hasInitialized) return;
    
    const draftIdFromUrl = searchParams.get('draftId');
    
    const finalizeLoad = () => {
      isLoadingDraftRef.current = false;
      setIsLoadingDraft(false);
      setHasInitialized(true);
    };
    
    const loadDraftFromServer = async (draftId: string) => {
      try {
        isLoadingDraftRef.current = true;
        setDraftSaleId(draftId);
        
        const draftSale = await retailSaleApi.get(draftId);
        if (draftSale && draftSale.status === 'draft') {
          if (draftSale.sale_date) form.setValue('sale_date', draftSale.sale_date);
          if (draftSale.seller_id) form.setValue('seller_id', draftSale.seller_id);
          if (draftSale.ponds) form.setValue('ponds', draftSale.ponds);
          if (draftSale.amount_received !== undefined) form.setValue('amount_received', draftSale.amount_received);
          if (draftSale.remarks) form.setValue('remarks', draftSale.remarks);
          
          if (draftSale.cartons && draftSale.cartons.length > 0) {
            while (fields.length > 0) {
              remove(0);
            }
            
            setTimeout(() => {
              const restoredCartons = draftSale.cartons!.map((c: CartonEntry) => ({
                ...c,
                quantity_kg: c.quantity_kg !== undefined ? Number(c.quantity_kg) : 0,
                rate_per_kg: c.rate_per_kg !== undefined ? Number(c.rate_per_kg) : 0,
                amount: c.amount !== undefined ? Number(c.amount) : 0,
                use_auto_rate: c.use_auto_rate !== undefined ? c.use_auto_rate : true,
              }));
              
              replace(restoredCartons);
              
              setTimeout(() => {
                const currentCartons = form.getValues('cartons') || [];
                const cartonsToSet = currentCartons.length > 0 ? currentCartons : restoredCartons;
                
                cartonsToSet.forEach((carton: CartonEntry, index: number) => {
                  const cartonData = restoredCartons[index] || carton;
                  
                  form.setValue(`cartons.${index}.quantity_kg`, cartonData.quantity_kg !== undefined ? Number(cartonData.quantity_kg) : 0, { shouldValidate: false });
                  form.setValue(`cartons.${index}.rate_per_kg`, cartonData.rate_per_kg !== undefined ? Number(cartonData.rate_per_kg) : 0, { shouldValidate: false });
                  form.setValue(`cartons.${index}.species`, cartonData.species || '', { shouldValidate: false });
                  form.setValue(`cartons.${index}.pond_id`, cartonData.pond_id || '', { shouldValidate: false });
                  form.setValue(`cartons.${index}.amount`, cartonData.amount !== undefined ? Number(cartonData.amount) : 0, { shouldValidate: false });
                  if (cartonData.carton_number) {
                    form.setValue(`cartons.${index}.carton_number`, cartonData.carton_number, { shouldValidate: false });
                  }
                  form.setValue(`cartons.${index}.use_auto_rate`, cartonData.use_auto_rate !== undefined ? cartonData.use_auto_rate : true, { shouldValidate: false });
                });
                
                setTimeout(() => {
                  const finalCartons = form.getValues('cartons') || [];
                  if (finalCartons.length > 0 && restoredCartons.length > 0) {
                    const lastIndex = finalCartons.length - 1;
                    const lastRestored = restoredCartons[lastIndex];
                    if (lastRestored) {
                      form.setValue(`cartons.${lastIndex}.quantity_kg`, lastRestored.quantity_kg !== undefined ? Number(lastRestored.quantity_kg) : 0, { shouldValidate: false });
                      form.setValue(`cartons.${lastIndex}.rate_per_kg`, lastRestored.rate_per_kg !== undefined ? Number(lastRestored.rate_per_kg) : 0, { shouldValidate: false });
                      form.setValue(`cartons.${lastIndex}.species`, lastRestored.species || '', { shouldValidate: false });
                      form.setValue(`cartons.${lastIndex}.pond_id`, lastRestored.pond_id || '', { shouldValidate: false });
                      form.setValue(`cartons.${lastIndex}.amount`, lastRestored.amount !== undefined ? Number(lastRestored.amount) : 0, { shouldValidate: false });
                    }
                  }
                  
                  const mergedDefaultRates = deriveDefaultRatesFromCartons(restoredCartons);
                  setDefaultRates(mergedDefaultRates);
                  
                  finalizeLoad();
                }, 300);
              }, 150);
            }, 100);
          } else {
            setDefaultRates({});
            finalizeLoad();
          }
          return true;
        }
      } catch (err) {
        console.error('Failed to load draft sale:', err);
        isLoadingDraftRef.current = false;
        setIsLoadingDraft(false);
      }
      return false;
    };
    
    const startLoad = async () => {
      if (draftIdFromUrl) {
        const loaded = await loadDraftFromServer(draftIdFromUrl);
        if (loaded) return;
      }
      finalizeLoad();
    };
    
    startLoad();
  }, [selectedLocation?.id, hasInitialized, searchParams]);

  // Build payload for draft saves
  const buildDraftPayload = () => {
    const formData = form.getValues();
    return {
      sale_date: formData.sale_date,
      seller_id: formData.seller_id,
      ponds: formData.ponds,
      cartons: (formData.cartons || []).map(c => ({
        carton_number: c.carton_number || undefined,
        pond_id: c.pond_id,
        species: c.species,
        quantity_kg: Number(c.quantity_kg) || 0,
        rate_per_kg: Number(c.rate_per_kg) || 0,
        amount: Number(c.amount) || 0,
        use_auto_rate: c.use_auto_rate,
      })),
      amount_received: Number(formData.amount_received) || 0,
      remarks: formData.remarks || undefined,
    };
  };

  // Save draft to backend (create/update) using latest form values
  const saveDraft = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    saveDraft.current = async () => {
      if (!selectedLocation?.id || isLoadingDraftRef.current) return;
      
      const formData = form.getValues();
      
      // Require minimal fields before creating/updating a draft
      if (!formData.seller_id || !formData.sale_date || !formData.cartons || formData.cartons.length === 0) {
        return;
      }

      const payload = buildDraftPayload();

      try {
        setSyncing(true);
        if (draftSaleId) {
          await retailSaleApi.updateDraft(draftSaleId, payload);
        } else {
          const draftSale = await retailSaleApi.createOnFarm({
            ...payload,
            location_id: selectedLocation.id,
          });
          setDraftSaleId(draftSale.id);
        }
        setLastSyncTime(new Date());
      } catch (err) {
        console.error('Failed to save draft:', err);
      } finally {
        setSyncing(false);
      }
    };
  }, [selectedLocation?.id, draftSaleId, form]);

  // Backfill defaultRates from crate rates if defaultRates are missing/zero.
  // This ensures Step 1.1 reflects rates already present in crates (e.g., after reload).
  useEffect(() => {
    if (!hasInitialized) return;
    const updated = { ...defaultRates };
    let changed = false;
    const cartons = form.getValues('cartons') || [];
    cartons.forEach((carton) => {
      if (!carton || !carton.species) return;
      const rate = Number(carton.rate_per_kg) || 0;
      if (rate > 0 && (!updated[carton.species] || updated[carton.species] === 0)) {
        updated[carton.species] = rate;
        changed = true;
      }
    });
    if (changed) {
      setDefaultRates(updated);
    }
  }, [hasInitialized, defaultRates, form]);

  // Auto-save to backend whenever the form changes (debounced)
  useEffect(() => {
    if (!selectedLocation?.id || !hasInitialized || isLoadingDraft || isLoadingDraftRef.current) return;
    
    const timeoutId = setTimeout(() => {
      saveDraft.current();
    }, 400); // slight debounce to batch rapid changes
    
    return () => clearTimeout(timeoutId);
  }, [watchedCartons, watchedPonds, watchedSellerId, watchedAmountReceived, form.watch('sale_date'), form.watch('remarks'), defaultRates, selectedLocation?.id, hasInitialized, isLoadingDraft, fields.length, form]);

  useEffect(() => {
    if (selectedLocation?.id) {
      loadSellers();
    }
  }, [selectedLocation?.id]);

  const loadSellers = async () => {
    if (!selectedLocation?.id) return;
    
    try {
      setLoadingSellers(true);
      const data = await sellerApi.list(selectedLocation.id);
      setAllSellers(data);
      // Filter sellers based on search term
      if (searchTerm.trim()) {
        const filtered = data.filter(seller => 
          seller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          seller.mobile.includes(searchTerm)
        );
        setSellers(filtered);
      } else {
        setSellers(data);
      }
    } catch (err: any) {
      console.error('Failed to load sellers:', err);
    } finally {
      setLoadingSellers(false);
    }
  };

  // Filter sellers when search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = allSellers.filter(seller => 
        seller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seller.mobile.includes(searchTerm)
      );
      setSellers(filtered);
    } else {
      setSellers(allSellers);
    }
  }, [searchTerm, allSellers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Get selected seller display name
  const selectedSeller = allSellers.find(s => s.id === form.watch('seller_id'));
  const displayValue = selectedSeller 
    ? `${selectedSeller.name} (${selectedSeller.mobile})`
    : searchTerm;

  const handleCreateSeller = async () => {
    if (!selectedLocation?.id || !newSellerName.trim() || !newSellerMobile.trim()) {
      setError('Name and mobile are required');
      return;
    }

    try {
      setCreatingSeller(true);
      setError('');
      const seller = await sellerApi.create({
        location_id: selectedLocation.id,
        name: newSellerName.trim(),
        mobile: newSellerMobile.trim(),
        email: newSellerEmail.trim() || undefined,
        address: newSellerAddress.trim() || undefined,
      });
      
      form.setValue('seller_id', seller.id);
      setShowCreateSeller(false);
      setShowDropdown(false);
      setNewSellerName('');
      setNewSellerMobile('');
      setNewSellerEmail('');
      setNewSellerAddress('');
      await loadSellers();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create seller'));
    } finally {
      setCreatingSeller(false);
    }
  };

  const addCrate = (pondId: string) => {
    const crateNumber = getNextCartonNumber(pondId);
    const newCrate = {
      carton_number: crateNumber.toString(),
      pond_id: pondId,
      species: '',
      quantity_kg: 0,
      rate_per_kg: 0,
      amount: 0,
      use_auto_rate: true, // Default to auto
    };
    append(newCrate);
    // Update crate number after append and save immediately
    setTimeout(() => {
      const currentCartons = form.getValues('cartons');
      const lastIndex = currentCartons.length - 1;
      if (lastIndex >= 0 && currentCartons[lastIndex].pond_id === pondId) {
        form.setValue(`cartons.${lastIndex}.carton_number`, crateNumber.toString());
      }
    }, 100);
  };

  const handleSpeciesChange = (crateIndex: number, species: string) => {
    form.setValue(`cartons.${crateIndex}.species`, species);
    const useAuto = form.getValues(`cartons.${crateIndex}.use_auto_rate`) !== false;
    
    // Check if this is a new species that doesn't have a default rate yet
    if (species && !(species in defaultRates)) {
      // New species added - open the default rates accordion
      setShowDefaultRates(true);
    }
    
    // Pre-fill rate from default rates if auto is enabled
    if (useAuto && defaultRates[species] && defaultRates[species] > 0) {
      form.setValue(`cartons.${crateIndex}.rate_per_kg`, defaultRates[species]);
    }
  };

  const handleAutoRateToggle = (crateIndex: number, checked: boolean) => {
    form.setValue(`cartons.${crateIndex}.use_auto_rate`, checked);
    const carton = watchedCartons[crateIndex];
    
    if (checked && carton.species && defaultRates[carton.species] && defaultRates[carton.species] > 0) {
      // Apply default rate
      form.setValue(`cartons.${crateIndex}.rate_per_kg`, defaultRates[carton.species]);
    }
    // If unchecked, keep current rate (allows manual editing)
  };

  const onSubmit = async (data: OnFarmSaleFormData) => {
    console.log('onSubmit called with data:', data);
    if (!selectedLocation?.id) {
      setError('Please select a location');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Filter out empty/invalid cartons before processing
      const validCartons = data.cartons.filter(c => 
        c && 
        c.pond_id && 
        c.species && 
        c.species.trim() !== '' &&
        c.quantity_kg && 
        Number(c.quantity_kg) > 0 &&
        c.rate_per_kg && 
        Number(c.rate_per_kg) > 0
      );

      if (validCartons.length === 0) {
        setError('Please add at least one valid fish crate with species, weight, and rate');
        setSubmitting(false);
        return;
      }

      const saleData = {
        location_id: selectedLocation.id,
        sale_date: data.sale_date,
        seller_id: data.seller_id,
        ponds: data.ponds,
        cartons: validCartons.map(c => ({
          carton_number: c.carton_number || undefined,
          pond_id: c.pond_id,
          species: c.species,
          quantity_kg: Number(c.quantity_kg),
          rate_per_kg: Number(c.rate_per_kg),
          amount: Number(c.amount) || (Number(c.quantity_kg) * Number(c.rate_per_kg)),
        })),
        amount_received: Number(data.amount_received) || 0,
        remarks: data.remarks || undefined,
      };

      console.log('Submitting sale data:', saleData);

      // If draft exists, update it with final sale data and mark as completed, otherwise create new
      if (draftSaleId) {
        console.log('Updating draft sale:', draftSaleId);
        // Update draft with final sale data first, then mark as completed
        await retailSaleApi.updateDraft(draftSaleId, saleData);
        await retailSaleApi.updateStatus(draftSaleId, { status: 'completed' });
      } else {
        console.log('Creating new sale');
        await retailSaleApi.createOnFarm(saleData);
      }
      
      setDraftSaleId(null);
      
      navigate('/retail-sales/list');
    } catch (err: any) {
      console.error('Error submitting sale:', err);
      setError(getErrorMessage(err, 'Failed to create sale'));
    } finally {
      setSubmitting(false);
    }
  };

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  const speciesInCartons = getSpeciesInCartons();

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/retail-sales" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">On-Farm Sale</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
        {lastSyncTime && (
          <div className="text-xs text-gray-500">
            {syncing ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" className="mr-1" />
                Syncing...
              </span>
            ) : (
              <span>Last synced: {lastSyncTime.toLocaleTimeString()}</span>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={(e) => {
        e.preventDefault();
        // Clean up empty/invalid cartons before validation
        const currentCartons = form.getValues('cartons');
        const validCartons: CartonEntry[] = [];
        const indicesToRemove: number[] = [];
        
        currentCartons.forEach((c, index) => {
          if (!c || !c.pond_id || !c.species || !c.species.trim() || 
              !c.quantity_kg || Number(c.quantity_kg) <= 0 ||
              !c.rate_per_kg || Number(c.rate_per_kg) <= 0) {
            indicesToRemove.push(index);
          } else {
            validCartons.push(c);
          }
        });
        
        // Remove invalid cartons in reverse order to maintain indices
        indicesToRemove.reverse().forEach(index => {
          remove(index);
        });
        
        // Update form with cleaned cartons
        if (indicesToRemove.length > 0) {
          form.setValue('cartons', validCartons);
        }
        
        // Now submit with cleaned data
        form.handleSubmit(onSubmit, (errors) => {
          console.log('Form validation errors:', errors);
          // Show specific validation errors
          const errorMessages: string[] = [];
          if (errors.cartons && Array.isArray(errors.cartons)) {
            errors.cartons.forEach((cartonError: any, index: number) => {
              if (cartonError) {
                if (cartonError.species) errorMessages.push(`Crate ${index + 1}: ${cartonError.species.message}`);
                if (cartonError.quantity_kg) errorMessages.push(`Crate ${index + 1}: ${cartonError.quantity_kg.message}`);
                if (cartonError.rate_per_kg) errorMessages.push(`Crate ${index + 1}: ${cartonError.rate_per_kg.message}`);
              }
            });
          }
          if (errors.seller_id) errorMessages.push('Seller is required');
          if (errors.sale_date) errorMessages.push('Sale date is required');
          if (errors.ponds) errorMessages.push('At least one pond is required');
          
          if (validCartons.length === 0) {
            setError('Please add at least one fish crate with species, weight, and rate');
          } else {
            setError(errorMessages.length > 0 ? errorMessages.join('. ') : 'Please fix the form errors before submitting');
          }
        })(e);
      }} className="space-y-6">
        {/* Step 1: Seller Selection */}
        <div className="card p-4 sm:p-6 space-y-4">
          <button
            type="button"
            onClick={() => setStep1Collapsed(!step1Collapsed)}
            className={`w-full flex items-center justify-between text-left ${watchedPonds.length > 0 ? 'cursor-pointer' : ''}`}
            disabled={watchedPonds.length === 0}
          >
            <h2 className="text-lg font-semibold text-gray-900">Step 1: Select Seller</h2>
            {watchedPonds.length > 0 && (
              step1Collapsed ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              )
            )}
          </button>
          
          {!step1Collapsed && (
          <div className="space-y-4">
          
          {!showCreateSeller ? (
            <div className="space-y-2">
              <div className="relative" ref={dropdownRef}>
                <label className="form-label">Seller <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or mobile number..."
                    value={displayValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      // If user is typing and it matches selected seller format exactly, don't clear
                      if (selectedSeller && value === `${selectedSeller.name} (${selectedSeller.mobile})`) {
                        return; // Don't update searchTerm, keep selected seller
                      }
                      // Update search term
                      setSearchTerm(value);
                      setShowDropdown(true);
                      // Clear selection if user starts typing something different
                      if (selectedSeller && value !== `${selectedSeller.name} (${selectedSeller.mobile})`) {
                        form.setValue('seller_id', '');
                        setDefaultRates({});
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className={`form-input pr-10 ${form.formState.errors.seller_id ? 'border-red-500' : ''}`}
                  />
                  <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
                
                {showDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {loadingSellers ? (
                      <div className="flex justify-center py-4">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : (searchTerm.trim() ? sellers : allSellers).length > 0 ? (
                      <>
                        {(searchTerm.trim() ? sellers : allSellers).map((seller) => (
                          <button
                            key={seller.id}
                            type="button"
                            onClick={() => {
                              form.setValue('seller_id', seller.id);
                              setSearchTerm('');
                              setShowDropdown(false);
                              // Trigger form validation
                              form.trigger('seller_id');
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                              form.watch('seller_id') === seller.id
                                ? 'bg-blue-50 border-l-4 border-blue-500'
                                : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900">{seller.name}</div>
                            <div className="text-sm text-gray-600">{seller.mobile}</div>
                          </button>
                        ))}
                        <div className="border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateSeller(true);
                              setShowDropdown(false);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-2 text-blue-600"
                          >
                            <PlusIcon className="h-5 w-5" />
                            <span>Create New Seller</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        {searchTerm.trim() ? 'No sellers found' : allSellers.length === 0 ? 'No sellers available' : 'Start typing to search...'}
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateSeller(true);
                            setShowDropdown(false);
                          }}
                          className="block mt-2 text-blue-600 hover:text-blue-800 flex items-center space-x-2"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span>Create New Seller</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {form.watch('seller_id') && (
                <div className="text-sm text-green-600">
                  ✓ Seller selected
                </div>
              )}
              
              {form.formState.errors.seller_id && (
                <p className="text-sm text-red-600">{form.formState.errors.seller_id.message}</p>
              )}
            </div>
          ) : (
            <div className="space-y-4 border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-gray-900">Create New Seller</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newSellerName}
                    onChange={(e) => setNewSellerName(e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Mobile <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newSellerMobile}
                    onChange={(e) => setNewSellerMobile(e.target.value)}
                    className="form-input"
                    placeholder="10-digit number"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={newSellerEmail}
                    onChange={(e) => setNewSellerEmail(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    value={newSellerAddress}
                    onChange={(e) => setNewSellerAddress(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleCreateSeller}
                  disabled={creatingSeller || !newSellerName.trim() || !newSellerMobile.trim()}
                  className="btn-primary flex-1"
                >
                  {creatingSeller ? 'Creating...' : 'Create Seller'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSeller(false);
                    setShowDropdown(false);
                    setNewSellerName('');
                    setNewSellerMobile('');
                    setNewSellerEmail('');
                    setNewSellerAddress('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          </div>
          )}
        </div>

        {/* Step 1.1: Set Default Rates for Fish Species - Independent Step */}
        {form.watch('seller_id') && speciesInCartons.length > 0 && (
          <div className="card p-4 sm:p-6 space-y-4">
            <button
              type="button"
              onClick={() => setShowDefaultRates(!showDefaultRates)}
              className="w-full flex items-center justify-between text-left"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                Step 1.1: Set Default Rates for Fish Species
              </h2>
              {showDefaultRates ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            
            {showDefaultRates && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Set default rates per kg for each fish species added in fish crates. These rates will be pre-filled when "Auto" is checked on crates, but can be changed at crate level for negotiation.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {speciesInCartons.map((species) => (
                    <div key={species}>
                      <label className="form-label">{species} (₹/kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={defaultRates[species] || 0}
                        onChange={(e) => {
                          const newRate = Number(e.target.value) || 0;
                          const updatedRates = {
                            ...defaultRates,
                            [species]: newRate
                          };
                          setDefaultRates(updatedRates);
                          
                          // Update all crates with this species that have auto enabled
                          watchedCartons.forEach((carton, index) => {
                            if (carton.species === species && carton.use_auto_rate) {
                              form.setValue(`cartons.${index}.rate_per_kg`, newRate);
                            }
                          });
                        }}
                        onBlur={(e) => {
                          // No-op; autosave effect will sync changes
                        }}
                        className="form-input"
                        placeholder="0.00"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Date and Ponds */}
        <div className="card p-4 sm:p-6 space-y-4">
          <button
            type="button"
            onClick={() => setStep2Collapsed(!step2Collapsed)}
            className={`w-full flex items-center justify-between text-left ${watchedPonds.length > 0 ? 'cursor-pointer' : ''}`}
            disabled={watchedPonds.length === 0}
          >
            <h2 className="text-lg font-semibold text-gray-900">Step 2: Select Date and Ponds</h2>
            {watchedPonds.length > 0 && (
              step2Collapsed ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              )
            )}
          </button>
          
          {!step2Collapsed && (
          <div className="space-y-4">
          
          <div>
            <label className="form-label">Sale Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              {...form.register('sale_date')}
              className={`form-input ${form.formState.errors.sale_date ? 'border-red-500' : ''}`}
            />
            {form.formState.errors.sale_date && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.sale_date.message}</p>
            )}
          </div>

          <div>
            <label className="form-label">Ponds <span className="text-red-500">*</span></label>
            <p className="text-sm text-gray-600 mb-2">
              Select all ponds that will be netted. Sales happen slowly (1-3 hours), one pond at a time.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {ponds?.map((pond) => (
                <label key={pond.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={pond.id}
                    {...form.register('ponds')}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{pond.name}</span>
                </label>
              ))}
            </div>
            {form.formState.errors.ponds && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.ponds.message}</p>
            )}
          </div>
          </div>
          )}
        </div>

        {/* Step 3: Fish Crates by Pond */}
        {watchedPonds.length > 0 && (
          <div className="card p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Step 3: Add Fish Crates</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Add fish crates for each pond. Crate numbers are auto-generated. Rates are pre-filled from default rates when "Auto" is checked, but can be changed for negotiation.
              </p>
            </div>

            {watchedPonds.map((pondId) => {
              const pond = ponds?.find(p => p.id === pondId);
              const pondCartons = getCartonsByPond(pondId);
              
              return (
                <div key={pondId} className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 gap-2">
                    <h3 className="text-sm sm:text-md font-semibold text-gray-900">
                      {pond?.name || 'Unknown Pond'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => addCrate(pondId)}
                      className="btn-secondary flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm py-1.5 sm:py-2"
                    >
                      <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span>Add Crate</span>
                    </button>
                  </div>

                  {pondCartons.length === 0 ? (
                    <div className="text-center py-2 sm:py-4 text-gray-500 text-xs sm:text-sm">
                      <p>No fish crates added yet. Click "Add Crate" to start.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {fields.map((field, fieldIndex) => {
                        const carton = watchedCartons[fieldIndex];
                        // Only show crates for this pond
                        if (!carton || carton.pond_id !== pondId) return null;
                        
                        // Calculate crate number within this pond (sequential)
                        const pondCartonsBefore = watchedCartons.slice(0, fieldIndex).filter(c => c.pond_id === pondId);
                        const crateNumber = pondCartonsBefore.length + 1;
                        const useAuto = carton.use_auto_rate !== false;
                        
                        return (
                          <div key={field.id} className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                            {/* Header Row: Crate #, Auto Checkbox, Total Amount, Delete Button */}
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                              <div className="flex items-center space-x-3">
                                <h4 className="text-xs sm:text-sm font-medium text-gray-900">
                                  Crate #{carton.carton_number || crateNumber}
                                </h4>
                                <label className="flex items-center space-x-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={useAuto}
                                    onChange={(e) => handleAutoRateToggle(fieldIndex, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                  />
                                  <span className="text-xs text-gray-600">Auto</span>
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="text-right">
                                  <div className="text-xs text-gray-500">Amount</div>
                                  <div className="text-xs sm:text-sm font-bold text-gray-900">
                                    ₹{((Number(carton.quantity_kg) || 0) * (Number(carton.rate_per_kg) || 0)).toFixed(2)}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => remove(fieldIndex)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Second Row: Species, Weight, Rate */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-3">
                              <div>
                                <label className="form-label text-xs">Species <span className="text-red-500">*</span></label>
                                <select
                                  {...form.register(`cartons.${fieldIndex}.species`)}
                                  onChange={(e) => handleSpeciesChange(fieldIndex, e.target.value)}
                                  className={`form-input text-xs sm:text-sm py-1.5 ${form.formState.errors.cartons?.[fieldIndex]?.species ? 'border-red-500' : ''}`}
                                >
                                  <option value="">Select</option>
                                  {uniqueFishSpecies.map((species) => (
                                    <option key={species.id} value={species.name}>
                                      {species.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="form-label text-xs">Weight (kg) <span className="text-red-500">*</span></label>
                                {(() => {
                                  const registerProps = form.register(`cartons.${fieldIndex}.quantity_kg`, { valueAsNumber: true });
                                  return (
                                    <input
                                      type="number"
                                      step="0.01"
                                      {...registerProps}
                                      onChange={(e) => {
                                        registerProps.onChange(e);
                                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        form.setValue(`cartons.${fieldIndex}.quantity_kg`, value, { shouldValidate: true, shouldDirty: true });
                                        // Trigger draft save immediately when weight changes
                                        void saveDraft.current();
                                      }}
                                      onBlur={(e) => {
                                        registerProps.onBlur(e);
                                      }}
                                      className={`form-input text-xs sm:text-sm py-1.5 ${form.formState.errors.cartons?.[fieldIndex]?.quantity_kg ? 'border-red-500' : ''}`}
                                    />
                                  );
                                })()}
                              </div>
                              <div>
                                <label className="form-label text-xs">Rate (₹/kg) <span className="text-red-500">*</span></label>
                                {(() => {
                                  const registerProps = form.register(`cartons.${fieldIndex}.rate_per_kg`, { valueAsNumber: true });
                                  return (
                                    <input
                                      type="number"
                                      step="0.01"
                                      {...registerProps}
                                      disabled={useAuto}
                                      onChange={(e) => {
                                        if (!useAuto) {
                                          registerProps.onChange(e);
                                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                          form.setValue(`cartons.${fieldIndex}.rate_per_kg`, value, { shouldValidate: true, shouldDirty: true });
                                        }
                                      }}
                                      onBlur={(e) => {
                                        if (!useAuto) {
                                          registerProps.onBlur(e);
                                        }
                                      }}
                                      className={`form-input text-xs sm:text-sm py-1.5 ${form.formState.errors.cartons?.[fieldIndex]?.rate_per_kg ? 'border-red-500' : ''} ${useAuto ? 'bg-gray-100' : ''}`}
                                      placeholder={useAuto ? "Auto" : "Rate"}
                                    />
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 4: Payment Summary */}
        {watchedCartons.length > 0 && (
          <div className="card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Step 4: Payment Summary</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                <label className="text-sm font-medium text-gray-600">Total Amount</label>
                <div className="text-2xl font-bold text-blue-600 mt-1">
                  ₹{totalAmount.toFixed(2)}
                </div>
              </div>
              <div>
                <label className="form-label">Amount Received (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  {...form.register('amount_received', { valueAsNumber: true })}
                  className="form-input"
                />
              </div>
              <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                <label className="text-sm font-medium text-gray-600">Amount Due</label>
                <div className={`text-2xl font-bold mt-1 ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{amountDue.toFixed(2)}
                </div>
              </div>
            </div>

            <div>
              <label className="form-label">Remarks</label>
              <textarea
                {...form.register('remarks')}
                rows={3}
                className="form-input"
                placeholder="Optional notes..."
              />
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Link
            to="/retail-sales"
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="button"
            disabled={syncing || isLoadingDraft || isLoadingDraftRef.current}
            onClick={async () => {
              await saveDraft.current();
            }}
            className="px-6 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-60"
          >
            {syncing ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
          >
            {submitting ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" className="mr-2" />
                Creating Sale...
              </span>
            ) : (
              'Create Sale'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnFarmSaleForm;
